
import { ConversationOverview, Conversation, Message, MessageRole, UserRating } from 'st-app/api'
import { AgentDocumentReferenceEvent, AgentRationaleEvent, AgentTicketReferenceEvent, parseTrace, requireSuppportAssistAgentContext, SuppportAssistAgentContext } from '../SupportAssistAgentContext'
import Button from '../../st-button/st-button';
import Dropdown from '../../st-dropdown/st-dropdown'
import styles from '../st-support-assist-agent.module.css'
import { useDropdownContext } from 'common';
import { createMemo, createSignal, For, Show } from 'solid-js'
import Flexbox from '../../st-flexbox/st-flexbox';
import Loader from '../../st-loader/st-loader';

function getTimeGroup(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const thisWeekStart = new Date(today); thisWeekStart.setDate(today.getDate() - today.getDay());
    const lastWeekStart = new Date(thisWeekStart); lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    if (date >= today) return 'Today';
    if (date >= yesterday) return 'Yesterday';
    if (date >= thisWeekStart) return 'This Week';
    if (date >= lastWeekStart) return 'Last Week';
    if (date >= thisMonthStart) return 'This Month';
    return 'Older';
}

export function Sidebar(props: { onCollapseClicked: (collapsed: boolean) => void }){
    const context = requireSuppportAssistAgentContext();

    const [collapsedGroups, setCollapsedGroups] = createSignal<Set<string>>(
        new Set(['Yesterday', 'This Week', 'Last Week', 'This Month', 'Older'])
    );

    const toggleGroup = (label: string) => setCollapsedGroups(prev => {
        const next = new Set(prev);
        if (next.has(label)) next.delete(label); else next.add(label);
        return next;
    });

    const grouped = createMemo(() => {
        const groups: { label: string; items: ConversationOverview[] }[] = [];
        const order = ['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Older'];
        const map = new Map<string, ConversationOverview[]>();

        for (const c of context.conversations) {
            const group = getTimeGroup(c.timestamp as string);
            if (!map.has(group)) map.set(group, []);
            map.get(group)!.push(c);
        }

        for (const label of order) {
            if (map.has(label)) {
                const items = map.get(label)!.sort(
                    (a, b) => new Date(b.timestamp as string).getTime() - new Date(a.timestamp as string).getTime()
                );
                groups.push({ label, items });
            }
        }
        return groups;
    });

    return (
        <div class={styles['conversation-list']}>
            <Button class={styles['conversation-list-item']} icon='fa-regular fa-chevron-left' type='ghost' onClick={() => props.onCollapseClicked(true)} />

            <Button class={styles['new-conversation']} type='ghost' onClick={() => context.startNewConversation()}>
                <i class='fa-regular fa-pen-to-square'></i> New Conversation
            </Button>

            <Show when={context.conversationsLoading()}>
                <Loader />
            </Show>

            <For each={grouped()}>{(group) => (
                <>
                    <button
                        class={styles['conversation-group-label']}
                        onClick={() => toggleGroup(group.label)}
                    >
                        <i class={`fa-regular fa-fw ${collapsedGroups().has(group.label) ? 'fa-chevron-right' : 'fa-chevron-down'}`} />
                        {group.label}
                    </button>
                    <Show when={!collapsedGroups().has(group.label)}>
                        <For each={group.items}>{(conversation) => (
                            <ConversationListItem conversation={conversation} />
                        )}</For>
                    </Show>
                </>
            )}</For>
        </div>
    )
}

export function ConversationListItem(props: { conversation: ConversationOverview }) {
    const context = requireSuppportAssistAgentContext();
    const isEditing = () => context.editingConversationGU() === props.conversation.conversationGU;

    const saveRename = (newTitle: string) => {
        const trimmed = newTitle.trim();
        context.setEditingConversationGU(null);
        if (trimmed && trimmed !== props.conversation.title) {
            context.renameConversation(props.conversation.conversationGU!, trimmed);
        }
    };

    return (
        <div class={styles['conversation-list-item']} onClick={() => { if (!isEditing()) context.loadConversation(props.conversation.conversationGU!); }}>
            <Show when={isEditing()} fallback={
                <span class={styles['conversation-title']} use:tooltip={props.conversation.title}>
                    {props.conversation.title}
                </span>
            }>
                <input
                    class={styles['conversation-title-input']}
                    value={props.conversation.title || ''}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            saveRename(e.currentTarget.value);
                        } else if (e.key === 'Escape') {
                            context.setEditingConversationGU(null);
                        }
                    }}
                    onBlur={(e) => saveRename(e.currentTarget.value)}
                    ref={(el) => requestAnimationFrame(() => { el.focus(); el.select(); })}
                />
            </Show>
            <Show when={!isEditing()}>
                <Dropdown>
                    <Dropdown.Trigger class='test' onClick={(e: MouseEvent) => e.stopPropagation()}>
                        <i class='fa-regular fa-ellipsis'></i>
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <ConversationMenu conversation={props.conversation}></ConversationMenu>
                    </Dropdown.Content>
                </Dropdown>
            </Show>
        </div>
    )
}

function ConversationMenu(props: { conversation: ConversationOverview }) {
    const context = requireSuppportAssistAgentContext();
    const dd = useDropdownContext();

    return (
        <Flexbox dir='vertical'>
            <Button icon='fa-regular fa-pen' type='ghost' aria-label='Rename conversation' onClick={() => { context.setEditingConversationGU(props.conversation.conversationGU!); dd?.toggle(false); }}>
                Rename
            </Button>
            <Button icon='fa-regular fa-trash-can' type='ghost' aria-label='Delete conversation' onClick={() => { context.deleteConversation(props.conversation.conversationGU!); dd?.toggle(false); }}>
                Delete
            </Button>
            <Button icon='fa-regular fa-arrow-up-from-bracket' type='ghost' onClick={() => { context.shareConversation(props.conversation.conversationGU!); dd?.toggle(false); }} aria-label='Copy conversation'>Share</Button>
        </Flexbox>
    )

}
